import { extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { getResponsiveStyle } from '../utils/styles/get-responsive-style.js';
import { media } from '../utils/styles/media.js';

const getSize = (col, breakpoint) => {
  if (!col) {
    return;
  }
  const withBreakpoint = style => breakpoint ? "".concat(media[breakpoint], " { ").concat(style, " }") : style;
  const getValue = size => {
    if (!size) {
      return withBreakpoint("\n        flex-basis: 0;\n        flex-grow: 1;\n        min-width: 0;\n        max-width: 100%;\n      ");
    }
    if (size === 'auto') {
      return withBreakpoint("\n        flex: 0 0 auto;\n        max-width: 100%;\n        width: auto;\n      ");
    }
    if (typeof size === 'boolean') {
      return withBreakpoint("\n        flex: 1 0 0%;\n      ");
    }
    return css(["width:100%;", ""], withBreakpoint("\n        flex: 0 0 ".concat(Math.round(size / 12 * 100 * 1000000) / 1000000, "%;\n        max-width: ").concat(Math.round(size / 12 * 100 * 1000000) / 1000000, "%;\n      ")));
  };
  return getValue(typeof col === 'object' ? col.size : col);
};
const StyledCol = styled.div.withConfig({
  componentId: "sg1390__sc-x8kwlu-0"
})(["", " ", " ", " ", " ", " ", " ", " ", " ", " position:", ";"], props => !props.$size.xs && !props.$size.sm && !props.$size.md && !props.$size.lg && !props.$size.xl && css(["flex-basis:0;flex-grow:1;min-width:0;max-width:100%;"]), props => getSize(props.$size.xs), props => getSize(props.$size.sm, 'sm'), props => getSize(props.$size.md, 'md'), props => getSize(props.$size.lg, 'lg'), props => getSize(props.$size.xl, 'xl'), props => getResponsiveStyle('align-self', props.$alignSelf), props => getResponsiveStyle('order', props.$order), props => getResponsiveStyle('margin-left', props.$offset), props => props.$relative && 'relative');
const getOffset = col => typeof col === 'object' && typeof col.offset === 'number' ? "".concat(Math.round(col.offset / 12 * 100 * 1000000) / 1000000, "%") : undefined;
const getAlignSelf = col => typeof col === 'object' && col.alignSelf ? col.alignSelf : undefined;
const Col = _ref => {
  let {
    as,
    children,
    className,
    lg,
    md,
    relative,
    sm,
    xl,
    xs,
    ...rest
  } = _ref;
  return /*#__PURE__*/React__default.createElement(StyledCol, _extends({
    $alignSelf: {
      xs: getAlignSelf(xs),
      sm: getAlignSelf(sm),
      md: getAlignSelf(md),
      lg: getAlignSelf(lg),
      xl: getAlignSelf(xl)
    },
    $offset: {
      xs: getOffset(xs),
      sm: getOffset(sm),
      md: getOffset(md),
      lg: getOffset(lg),
      xl: getOffset(xl)
    },
    $order: {
      xs: typeof xs === 'object' ? xs.order : undefined,
      sm: typeof sm === 'object' ? sm.order : undefined,
      md: typeof md === 'object' ? md.order : undefined,
      lg: typeof lg === 'object' ? lg.order : undefined,
      xl: typeof xl === 'object' ? xl.order : undefined
    },
    $relative: relative,
    $size: {
      xs,
      sm,
      md,
      lg,
      xl
    },
    as: as,
    className: className
  }, rest), children);
};
const ALIGN_SELF = ['start', 'center', 'end', 'baseline', 'stretch'];
const COLS_WITH_AUTO = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 'auto'];
const OFFSET = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const ORDER = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 'first', 'last'];
Col.displayName = 'Col';
Col.propTypes = {
  /**
   * Element or component used for the root node.
   */
  as: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string]),
  /**
   * Content of the component.
   */
  children: PropTypes.node,
  className: PropTypes.string,
  lg: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(COLS_WITH_AUTO), PropTypes.shape({
    alignSelf: PropTypes.oneOf(ALIGN_SELF),
    size: PropTypes.oneOf(COLS_WITH_AUTO),
    offset: PropTypes.oneOf(OFFSET),
    order: PropTypes.oneOf(ORDER)
  })]),
  md: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(COLS_WITH_AUTO), PropTypes.shape({
    alignSelf: PropTypes.oneOf(ALIGN_SELF),
    size: PropTypes.oneOf(COLS_WITH_AUTO),
    offset: PropTypes.oneOf(OFFSET),
    order: PropTypes.oneOf(ORDER)
  })]),
  relative: PropTypes.bool,
  sm: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(COLS_WITH_AUTO), PropTypes.shape({
    alignSelf: PropTypes.oneOf(ALIGN_SELF),
    size: PropTypes.oneOf(COLS_WITH_AUTO),
    offset: PropTypes.oneOf(OFFSET),
    order: PropTypes.oneOf(ORDER)
  })]),
  xl: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(COLS_WITH_AUTO), PropTypes.shape({
    alignSelf: PropTypes.oneOf(ALIGN_SELF),
    size: PropTypes.oneOf(COLS_WITH_AUTO),
    offset: PropTypes.oneOf(OFFSET),
    order: PropTypes.oneOf(ORDER)
  })]),
  xs: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(COLS_WITH_AUTO), PropTypes.shape({
    alignSelf: PropTypes.oneOf(ALIGN_SELF),
    size: PropTypes.oneOf(COLS_WITH_AUTO),
    offset: PropTypes.oneOf(OFFSET),
    order: PropTypes.oneOf(ORDER)
  })])
};

export { Col, StyledCol };
